import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Space,
  Typography,
  Row,
  Col,
  Upload,
  Progress,
  Radio,
  Divider
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
  DownloadOutlined,
  StopOutlined,
  FileZipOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text } = Typography;

const BASE_URL = 'https://ds2.cn/ds';

interface UrbanLegendType {
  id: number;
  code: string;
  name: string;
  created: string;
  updated: string;
  instances: UrbanLegendInstance[];
}

interface UrbanLegendInstance {
  id: number;
  hash: string;
  healthBonus: number;
  speedBonus: number;
  skillBonus: number;
  enhancementLevel: number | null;
  created: string;
}

const UrbanLegendAdmin: React.FC = () => {
  const [types, setTypes] = useState<UrbanLegendType[]>([]);
  const [loading, setLoading] = useState(false);
  
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [batchGenerateModalVisible, setBatchGenerateModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  
  const [selectedType, setSelectedType] = useState<UrbanLegendType | null>(null);
  const [uploadData, setUploadData] = useState<Array<{ code: string, name: string }>>([]);
  
  // 下载相关状态
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const [downloadController, setDownloadController] = useState<AbortController | null>(null);
  
  const [typeForm] = Form.useForm();
  const [generateForm] = Form.useForm();
  const [batchGenerateForm] = Form.useForm();

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/urban-legend/types');
      const data = await response.json();
      if (data.success) {
        setTypes(data.data || []);
      } else {
        message.error(data.message || '获取都市传说种类失败');
      }
    } catch (error) {
      message.error('获取都市传说种类失败');
    } finally {
      setLoading(false);
    }
  };

  const createType = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/urban-legend/types', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: values.code,
          name: values.name
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success('创建都市传说种类成功');
        setTypeModalVisible(false);
        typeForm.resetFields();
        fetchTypes();
      } else {
        message.error(data.message || '创建都市传说种类失败');
      }
    } catch (error) {
      message.error('创建都市传说种类失败');
    } finally {
      setLoading(false);
    }
  };

  const generateInstances = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/urban-legend/types/${selectedType?.id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity: values.quantity
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success(`成功生成 ${values.quantity} 个都市传说实例`);
        setGenerateModalVisible(false);
        generateForm.resetFields();
        fetchTypes();
        
        if (data.data && data.data.instances && data.data.instances.length > 0) {
          exportHashList(data.data.instances, data.data.code);
        }
      } else {
        message.error(data.message || '生成都市传说实例失败');
      }
    } catch (error) {
      message.error('生成都市传说实例失败');
    } finally {
      setLoading(false);
    }
  };

  // 取消下载
  const cancelDownload = () => {
    if (downloadController) {
      downloadController.abort();
      setDownloadController(null);
    }
    setDownloading(false);
    setDownloadProgress({ current: 0, total: 0 });
    message.info('下载已取消');
  };

  // 分批下载文件
  const downloadInBatches = async (files: { code: string, content: string }[], batchSize = 5, delay = 1000) => {
    const controller = new AbortController();
    setDownloadController(controller);
    setDownloading(true);
    setDownloadProgress({ current: 0, total: files.length });

    try {
      for (let i = 0; i < files.length; i += batchSize) {
        if (controller.signal.aborted) {
          throw new Error('Download cancelled');
        }

        const batch = files.slice(i, i + batchSize);
        
        // 并行下载当前批次的文件
        await Promise.all(batch.map(async (file, index) => {
          if (controller.signal.aborted) return;
          
          const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
          const blob = new Blob([BOM, file.content], { type: 'text/plain;charset=utf-8' });
          const fileName = `code_${file.code}.txt`;
          saveAs(blob, fileName);
          
          // 更新进度
          setDownloadProgress(prev => ({ ...prev, current: i + index + 1 }));
        }));

        // 批次间延迟，避免浏览器阻塞
        if (i + batchSize < files.length && !controller.signal.aborted) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      if (!controller.signal.aborted) {
        message.success(`成功下载 ${files.length} 个文件`);
      }
    } catch (error: any) {
      if (error.message !== 'Download cancelled') {
        message.error('下载过程中出现错误');
        console.error('Download error:', error);
      }
    } finally {
      setDownloading(false);
      setDownloadController(null);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  // ZIP打包下载
  const downloadAsZip = async (files: { code: string, content: string }[]) => {
    try {
      setLoading(true);
      
      // 动态导入JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // 添加文件到ZIP
      files.forEach(file => {
        zip.file(`code_${file.code}.txt`, `\ufeff${file.content}`, { binary: false });
      });

      // 生成ZIP文件
      const content = await zip.generateAsync({ type: 'blob' });
      const fileName = `urban_legend_batch_${dayjs().format('YYYYMMDD_HHmmss')}.zip`;
      saveAs(content, fileName);
      
      message.success(`成功生成ZIP文件包含 ${files.length} 个文件`);
    } catch (error) {
      message.error('生成ZIP文件失败');
      console.error('ZIP generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const batchGenerateInstances = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/urban-legend/types/batch-generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quantity: values.quantity })
      });

      const data = await response.json();
      if (data.success) {
        message.success('批量生成成功');
        setBatchGenerateModalVisible(false);
        batchGenerateForm.resetFields();
        
        const fileData = data.data.fileData;
        
        // 根据用户选择的下载方式处理
        if (values.downloadType === 'zip') {
          await downloadAsZip(fileData);
        } else {
          // 分批下载，根据文件数量调整批次大小
          const batchSize = fileData.length > 100 ? 3 : fileData.length > 50 ? 5 : 10;
          const delay = fileData.length > 100 ? 2000 : 1000;
          await downloadInBatches(fileData, batchSize, delay);
        }
        
        fetchTypes();
      } else {
        message.error(data.message || '批量生成失败');
      }
    } catch (error) {
      message.error('批量生成失败');
    } finally {
      setLoading(false);
    }
  };

  const exportHashList = (instances: any[], code: string) => {
    const content = instances.map(instance => `${BASE_URL}/${instance.hash}`).join('\r\n') + '\r\n';
    
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
    const fileName = `code_${code}.txt`;
    saveAs(blob, fileName);
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        const validData: Array<{ code: string, name: string }> = [];
        
        // 跳过标题行，从第二行开始处理
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as any[];
          if (row && row[0] && row[1]) {
            const code = String(row[0]).trim();
            const name = String(row[1]).trim();
            if (code && name) {
              validData.push({ code, name });
            }
          }
        }
        
        setUploadData(validData);
        message.success(`成功导入${validData.length}条都市传说种类数据`);
      } catch (error) {
        console.error('解析文件时出错:', error);
        message.error('解析表格文件失败，请检查文件格式');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // 阻止自动上传
  };

  const uploadToServer = async () => {
    if (uploadData.length === 0) {
      message.warning('没有可上传的数据');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    
    try {
      for (const item of uploadData) {
        try {
          const response = await fetch('/api/v1/urban-legend/types', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code: item.code, name: item.name })
          });
          
          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
            console.error(`创建失败 (编号: ${item.code}):`, data.message);
          }
        } catch (error) {
          failCount++;
          console.error(`创建失败 (编号: ${item.code}):`, error);
        }
      }
      
      message.success(`批量创建完成: 成功 ${successCount} 个, 失败 ${failCount} 个`);
      
      if (successCount > 0) {
        fetchTypes(); // 刷新数据
        setUploadModalVisible(false);
        setUploadData([]);
      }
    } catch (error) {
      message.error('批量创建失败');
      console.error('批量创建错误:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['编号', '名称'],
      ['001', '影子人'],
      ['002', '电梯游戏'],
      ['003', '镜中人']
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '都市传说种类模板');
    
    // 设置列宽
    ws['!cols'] = [{ wch: 10 }, { wch: 20 }];
    
    XLSX.writeFile(wb, '都市传说种类导入模板.xlsx');
    message.success('模板下载成功');
  };

  const typeColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '编号',
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '实例数量',
      key: 'instanceCount',
      render: (record: UrbanLegendType) => (
        <Text>{record.instances?.length || 0}</Text>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (record: UrbanLegendType) => (
        <Space size="middle">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedType(record);
              setGenerateModalVisible(true);
            }}
          >
            生成实例
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="urban-legend-admin">
      <Title level={2}>都市传说管理</Title>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setTypeModalVisible(true)}
            >
              新增都市传说种类
            </Button>
          </Col>
          <Col>
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              批量导入
            </Button>
          </Col>
          <Col>
            <Button
              type="default"
              onClick={() => setBatchGenerateModalVisible(true)}
            >
              批量生成
            </Button>
          </Col>
          <Col>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
            >
              下载模板
            </Button>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTypes}
              loading={loading}
            >
              刷新数据
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={types}
          columns={typeColumns}
          rowKey="id"
          loading={loading}
          pagination={{ 
            pageSize: 10, 
            showSizeChanger: false,
            showQuickJumper: false
          }}
          scroll={{ x: 'max-content' }}
          size="middle"
          bordered={false}
          showHeader={true}
        />
      </Card>

      <Modal
        title="新增都市传说种类"
        open={typeModalVisible}
        onCancel={() => {
          setTypeModalVisible(false);
          typeForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={typeForm}
          layout="vertical"
          onFinish={createType}
        >
          <Form.Item
            name="code"
            label="编号"
            rules={[{ required: true, message: '请输入编号' }]}
          >
            <Input placeholder="请输入编号（如：001）" />
          </Form.Item>

          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="请输入都市传说名称" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                创建
              </Button>
              <Button
                onClick={() => {
                  setTypeModalVisible(false);
                  typeForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`生成都市传说实例 - 编号: ${selectedType?.code}`}
        open={generateModalVisible}
        onCancel={() => {
          setGenerateModalVisible(false);
          generateForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={generateForm}
          layout="vertical"
          onFinish={generateInstances}
        >
          <Form.Item
            name="quantity"
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber min={1} max={10000} placeholder="请输入生成数量" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                生成并导出
              </Button>
              <Button
                onClick={() => {
                  setGenerateModalVisible(false);
                  generateForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量生成都市传说实例"
        open={batchGenerateModalVisible}
        onCancel={() => {
          setBatchGenerateModalVisible(false);
          batchGenerateForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={batchGenerateForm}
          layout="vertical"
          onFinish={batchGenerateInstances}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              将为所有都市传说种类各生成指定数量的实例，
              并自动下载多个TXT文件，每个编号对应一个文件（如：code_001.txt、code_002.txt），
              文件内容为该编号的所有扫码地址，末尾包含一个空行。
            </Text>
          </div>

          <Form.Item
            name="quantity"
            label="每种都市传说生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber
              min={1}
              max={10000}
              placeholder="请输入每种都市传说生成的数量"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="downloadType"
            label="下载方式"
            initialValue="batch"
            rules={[{ required: true, message: '请选择下载方式' }]}
          >
            <Radio.Group>
              <Radio value="batch">分批下载（推荐大数据量使用）</Radio>
              <Radio value="zip">ZIP打包下载（推荐小数据量使用）</Radio>
            </Radio.Group>
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              • 分批下载：文件较多时自动分批下载，减少浏览器卡顿，支持取消操作<br/>
              • ZIP打包：将所有文件打包成一个ZIP文件下载，适合文件数量较少时使用
            </Text>
          </div>

          {/* 下载进度显示 */}
          {downloading && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>正在下载文件...</Text>
                <Text>{downloadProgress.current}/{downloadProgress.total}</Text>
              </div>
              <Progress 
                percent={downloadProgress.total > 0 ? Math.round((downloadProgress.current / downloadProgress.total) * 100) : 0}
                status="active"
              />
            </div>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={downloading}
                icon={loading ? undefined : <DownloadOutlined />}
              >
                {loading ? '生成中...' : '开始批量生成'}
              </Button>
              
              {downloading && (
                <Button
                  danger
                  onClick={cancelDownload}
                  icon={<StopOutlined />}
                >
                  取消下载
                </Button>
              )}
              
              <Button
                onClick={() => {
                  setBatchGenerateModalVisible(false);
                  batchGenerateForm.resetFields();
                  if (downloading) {
                    cancelDownload();
                  }
                }}
                disabled={loading && !downloading}
              >
                关闭
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量导入都市传说种类"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setUploadData([]);
        }}
        footer={null}
        width={700}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">
              请先下载模板，按照模板格式填写数据后再上传。支持 .xlsx、.xls 格式文件。
            </Text>
          </div>
          
          <Space>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleFileUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
            
            <Button 
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
            >
              下载模板
            </Button>
          </Space>

          {uploadData.length > 0 && (
            <>
              <div>
                <Text strong>已导入数据预览 ({uploadData.length} 条)</Text>
              </div>
              
              <Table
                dataSource={uploadData.map((item, index) => ({ ...item, key: index }))}
                columns={[
                  {
                    title: '编号',
                    dataIndex: 'code',
                    key: 'code',
                  },
                  {
                    title: '名称',
                    dataIndex: 'name',
                    key: 'name',
                  }
                ]}
                pagination={{ pageSize: 10 }}
                size="small"
                style={{ maxHeight: '300px', overflow: 'auto' }}
              />

              <Space>
                <Button 
                  type="primary"
                  onClick={uploadToServer}
                  loading={loading}
                >
                  确认导入到服务器
                </Button>
                <Button 
                  onClick={() => {
                    setUploadModalVisible(false);
                    setUploadData([]);
                  }}
                >
                  取消
                </Button>
              </Space>
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default UrbanLegendAdmin;